import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PublicFooter } from "@/components/PublicShell";
import { PublicMobileNav } from "@/components/PublicMobileNav";
import {
  FileSpreadsheet, FileCode2, CheckCircle2, ArrowRight, ShieldCheck, Upload,
  ScanLine, ReceiptText, Layers, Zap, Server,
  Building2, PiggyBank, BadgeCheck, Handshake,
} from "lucide-react";

const PLANS = [
  { key: "payg", name: "Pay as you go", price: "0.15", unit: "AED / invoice", sub: "No annual commitment", perks: ["Billed only when you invoice", "Full compliant exports", "Unlimited contracts & IPCs"] },
  { key: "starter", name: "Starter", price: "100", unit: "AED / year", sub: "Up to 1,000 invoices", perks: ["1,000 invoices / year", "Then AED 0.15 each", "Full compliant exports"] },
  { key: "growth", name: "Growth", price: "300", unit: "AED / year", sub: "Up to 3,000 invoices", popular: true, perks: ["3,000 invoices / year", "Then AED 0.15 each", "Priority support"] },
  { key: "pro", name: "Pro", price: "600", unit: "AED / year", sub: "Up to 6,000 invoices", perks: ["6,000 invoices / year", "Then AED 0.15 each", "Best value for high volume"] },
];
// Manual Submission pricing tiers are hidden from users (retained in code). Only Full Service is shown.
const SHOW_MANUAL = false;

const STEPS = [
  { icon: ReceiptText, title: "Pick your invoice type", body: "A standard tax invoice for any business, or a construction contract billed through Interim Payment Certificates. Your company details come from your profile." },
  { icon: ScanLine, title: "Add your details", body: "Standard: enter the customer and line items with per-line VAT (5%, zero-rated or exempt). Construction: set the cumulative % complete against each BOQ line, with advance recovery and retention." },
  { icon: ShieldCheck, title: "Review the totals", body: "Matiqs computes the subtotal, discount, 5% VAT and the amount in words, and flags anything to check — the same engine for both invoice types." },
  { icon: FileCode2, title: "Generate the compliant invoice", body: "Get a branded PDF (logo + verify QR), Excel, CSV and an optional Peppol UBL 2.1 XML — ready for your ASP." },
];

const OFFERINGS = [
  {
    icon: Building2,
    tag: "No ERP",
    title: "No ERP? Start here.",
    body: "Skip the six-figure ERP. Matiqs Qonnect is a complete, standalone UAE e-invoicing workspace for any business — add your line items and bill the compliant way from day one.",
    cta: "Choose Matiqs Qonnect",
  },
  {
    icon: PiggyBank,
    tag: "Have an ERP",
    title: "Have an ERP? Save the add-on cost.",
    body: "Avoid expensive e-invoicing modules, integration projects and extra licence fees. Raise your invoices on Matiqs Qonnect alongside your ERP — at a fraction of the cost.",
    cta: "Choose Matiqs Qonnect",
  },
  {
    icon: BadgeCheck,
    tag: "Have an ASP",
    title: "Already have an ASP? Pre-validate first.",
    body: "Catch construction-specific errors — BOQ mismatches, over-certified quantities, advance recovery and retention — before they ever reach your ASP. Matiqs is your safeguard.",
    cta: "Matiqs is here",
  },
  {
    icon: Handshake,
    tag: "Need an ASP",
    title: "Need an ASP? We'll connect you to an Approved ASP.",
    body: "Have an ERP but no Accredited Service Provider yet? Matiqs Qonnect is a lightweight ERP that partners with an Approved ASP — generate compliant files today and submit through the connected ASP as it rolls out. The partnership is designed to bring more affordable e-invoicing to SMBs and SMEs.",
    cta: "Partners available",
  },
];

const PublicHeader = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const appHome = user?.account_type === "contractor" ? "/marketplace" : user?.account_type === "einvoice" ? "/einvoicing/app" : "/dashboard";
  return (
    <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="einv-brand" className="flex items-center gap-3 shrink-0">
          <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto" />
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          <Link to="/" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground">Home</Link>
          <Link to="/einvoicing" className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-secondary text-foreground">Matiqs Qonnect</Link>
          <Link to="/pricing" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground">Pricing</Link>
          <Link to="/how-it-works" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground">How it works</Link>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <Button data-testid="einv-signin" variant="outline" onClick={() => navigate(user ? "/einvoicing/app" : "/login")} className="hidden md:flex h-10 font-semibold">
            {user ? "Open workspace" : "Sign in"}
          </Button>
          <PublicMobileNav />
        </div>
      </div>
    </header>
  );
};

export default function EInvoicingService() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const start = () => navigate(user ? "/einvoicing/app" : "/login?mode=register&type=einvoice");

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="einvoicing-service-page">
      <PublicHeader />

      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-fade pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 py-16 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold uppercase tracking-[0.16em] border border-emerald-300/50">
                <ShieldCheck className="w-3.5 h-3.5" /> UAE FTA · Peppol PINT-AE
              </span>
              <span data-testid="jam-partner-badge" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold uppercase tracking-[0.16em] border border-indigo-300/50">
                <Handshake className="w-3.5 h-3.5" /> Proud partner · Approved ASP
              </span>
            </div>
            <img src="/matiqs-qonnect-logo.png" alt="Matiqs Qonnect" data-testid="qonnect-hero-logo" className="brand-logo h-11 w-auto mt-5 mb-1" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.05] text-slate-900 dark:text-white mt-3">
              UAE e-invoicing for<br />every business,<br /><span className="text-primary">without the ERP.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-5 max-w-xl">
              Create FTA-compliant tax invoices in minutes — freelancers, service firms, retailers, traders,
              agencies and SMEs. Add your line items and export as PDF, Excel, CSV and Peppol UBL XML. Billing
              a construction contract? Invoice every Interim Payment Certificate too. A lightweight, affordable
              ERP alternative that submits each invoice directly to the FTA for you through our Approved
              Service Provider partner — so smaller businesses save on cost.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Button data-testid="einv-hero-cta" onClick={start} className="h-12 px-7 text-base font-bold gap-2">
                Start e-invoicing <ArrowRight className="w-4 h-4" />
              </Button>
              <a href="#pricing" className="h-12 px-6 inline-flex items-center rounded-lg border border-border font-semibold text-sm hover:bg-secondary transition-colors">
                See pricing
              </a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Standard &amp; construction invoices</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> 5% VAT, zero-rated &amp; exempt</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Peppol UBL 2.1 XML</span>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl border border-border bg-card shadow-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="font-mono text-sm font-bold text-slate-900 dark:text-white">TAX INVOICE · INV-2026-0042</div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded">Compliant</span>
              </div>
              {[["Consulting — 10 hrs", "AED 5,000"], ["Software licence", "AED 2,000"], ["Subtotal", "AED 7,000"], ["VAT @ 5%", "AED 350"]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm border-b border-dashed border-border pb-2">
                  <span className="text-muted-foreground">{k}</span><span className="font-semibold text-slate-900 dark:text-white">{v}</span>
                </div>
              ))}
              <div className="flex items-center justify-between bg-primary/10 rounded-lg px-3 py-2.5">
                <span className="font-bold text-slate-900 dark:text-white">Grand total</span>
                <span className="font-extrabold text-primary">AED 7,350</span>
              </div>
              <div className="flex gap-2 pt-1">
                {[["Excel", FileSpreadsheet], ["CSV", FileSpreadsheet], ["PDF", ReceiptText], ["UBL XML", FileCode2]].map(([l, Ic]) => (
                  <span key={l} className="flex-1 flex items-center justify-center gap-1 text-[11px] font-semibold border border-border rounded-md py-1.5 text-muted-foreground">
                    <Ic className="w-3.5 h-3.5" /> {l}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Submission via Full Service */}
      <section className="max-w-6xl mx-auto px-5 py-16 w-full">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">How your invoices reach the FTA</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">Matiqs produces the compliant files and submits them directly to the FTA for you.</p>
        </div>
        <div className="max-w-2xl mx-auto">
          <div data-testid="einv-option-2" className="rounded-2xl border border-indigo-300 dark:border-indigo-800 bg-card p-7 relative ring-1 ring-indigo-200/60 dark:ring-indigo-900/40">
            <span className="absolute top-5 right-5 text-[10px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded">Full Service</span>
            <div className="w-11 h-11 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4"><Server className="w-5 h-5 text-indigo-500" /></div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-500">Connected ASP</div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-1.5">Let Matiqs Qonnect submit for you</h3>
            <p className="text-sm text-muted-foreground mt-2">Matiqs produces the Excel, CSV, certificate PDF and Peppol UBL XML, then submits each invoice directly through <strong>our Approved Service Provider partner</strong> — with clearance status tracked against each invoice. Matiqs Qonnect stays the lightweight ERP; our Approved ASP does the accredited transmission, and the partnership is intended to keep e-invoicing affordable for SMBs and SMEs. Available on the Full Service plan.</p>
            <ul className="mt-4 space-y-2 text-sm">
              {["One-click submission through our Approved ASP", "Live clearance tracking", "No manual ASP uploads"].map((p) => (
                <li key={p} className="flex items-center gap-2 text-slate-700 dark:text-slate-300"><CheckCircle2 className="w-4 h-4 text-indigo-400 shrink-0" /> {p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What Matiqs can offer */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-6xl mx-auto px-5 py-16 w-full">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-soft text-brand text-[11px] font-bold uppercase tracking-[0.16em]">What Matiqs offers</span>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-4">Wherever you are, Matiqs fits in</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">From no system at all to a full ERP with an ASP — there's a reason to run your construction e-invoicing through Matiqs.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {OFFERINGS.map((o) => (
              <div key={o.title} data-testid={`einv-offer-${o.tag.toLowerCase().replace(/\s+/g, "-")}`}
                   className="rounded-2xl border border-border bg-card p-6 flex flex-col hover:border-primary/40 hover:shadow-md transition-all">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4"><o.icon className="w-5 h-5 text-primary" /></div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{o.tag}</div>
                <h3 className="font-bold text-slate-900 dark:text-white mt-1 leading-snug">{o.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 flex-1">{o.body}</p>
                <div className="flex items-center gap-1.5 mt-4 text-sm font-bold text-primary">
                  <CheckCircle2 className="w-4 h-4" /> {o.cta}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border bg-secondary/30">
        <div className="max-w-6xl mx-auto px-5 py-16 w-full">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">From BOQ to compliant invoice</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.title} className="rounded-2xl border border-border bg-card p-6">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3"><s.icon className="w-5 h-5 text-primary" /></div>
                <div className="text-[11px] font-bold text-muted-foreground">STEP {i + 1}</div>
                <h3 className="font-bold text-slate-900 dark:text-white mt-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 py-16 w-full">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-brand-soft text-brand text-[11px] font-bold uppercase tracking-[0.16em]">E-invoicing pricing</span>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-4">Fully managed e-invoicing</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">Let Matiqs submit every invoice directly to the FTA through our Approved Service Provider partner — with live clearance tracking. One simple Full Service plan, independent of the marketplace.</p>
        </div>

        {/* Manual Submission tier — hidden from users, retained in code */}
        {SHOW_MANUAL && (<>
        {/* SMB affordability strip */}
        <div data-testid="smb-pricing-strip" className="rounded-2xl border border-border bg-gradient-to-br from-brand-soft/60 to-card p-6 sm:p-7 mb-10">
          <div className="grid sm:grid-cols-2 gap-6 items-center">
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-2 text-brand"><PiggyBank className="w-5 h-5" /><span className="text-[11px] font-bold uppercase tracking-[0.16em]">Built for SMB &amp; SME budgets</span></div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-4xl font-extrabold text-slate-900 dark:text-white">AED 0.15</span>
                <span className="text-sm font-semibold text-muted-foreground">/ invoice</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">Pay as you go, or an annual package from just AED 100/year — no onboarding, no platform fee, no setup. An affordable ERP alternative that helps smaller businesses save.</div>
            </div>
            <ul className="space-y-2.5 text-sm text-slate-700 dark:text-slate-300">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> From AED 0.15 per invoice — no ERP licences</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> Annual packages from AED 100/year, up to 6,000 invoices</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> FTA-compliant files, ASP-ready via our ASP partnership</li>
            </ul>
          </div>
        </div>

        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground mb-3">Manual Submission — you submit to your own ASP</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`einv-plan-${p.key}`}
                 className={`rounded-2xl border bg-card p-6 flex flex-col ${p.popular ? "border-primary ring-2 ring-primary/30" : "border-border"}`}>
              {p.popular && <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-2">Most popular</div>}
              <h3 className="font-bold text-slate-900 dark:text-white">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold text-slate-900 dark:text-white">{p.price}</span>
                <span className="text-xs text-muted-foreground font-semibold">{p.unit}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{p.sub}</div>
              <ul className="mt-4 space-y-2 text-sm flex-1">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-slate-700 dark:text-slate-300"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {perk}</li>
                ))}
              </ul>
              <Button data-testid={`einv-plan-cta-${p.key}`} onClick={start} variant={p.popular ? "default" : "outline"} className="w-full mt-5 font-semibold">
                Get started
              </Button>
            </div>
          ))}
        </div>
        </>)}

        {/* Full Service */}
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground mt-10 mb-3">Full Service — Matiqs submits directly to the ASP</div>
        <div data-testid="einv-plan-full_service" className="rounded-2xl border border-indigo-300/50 bg-gradient-to-br from-indigo-50/60 dark:from-indigo-950/20 to-card p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white text-lg">Full Service</h3>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">We submit each invoice directly to the Approved ASP on your behalf, with live clearance tracking — no manual uploads. No invoice volume limit.</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-sm">
                <span className="text-slate-700 dark:text-slate-300"><strong>Pricing: To be confirmed</strong></span>
              </div>
            </div>
            <Button data-testid="einv-plan-cta-full_service" onClick={start} className="font-semibold gap-1.5">Get Full Service <ArrowRight className="w-4 h-4" /></Button>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">Matiqs Qonnect is a lightweight, affordable ERP alternative that generates compliant documents and partners with Accredited Service Providers who transmit invoices to the FTA — intended to help SMBs and SMEs save on e-invoicing. Connected submission through our Approved ASP is available on the Full Service plan.</p>
      </section>

      {/* Construction marketplace callout */}
      <section className="max-w-6xl mx-auto px-5 w-full">
        <a href="/marketplace" data-testid="landing-marketplace-callout"
           className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-2xl border border-indigo-300/50 bg-indigo-50/50 dark:bg-indigo-950/20 px-6 py-5 hover:border-indigo-400 transition-colors">
          <div className="flex items-start gap-3">
            <Building2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-slate-900 dark:text-white">Construction business?</div>
              <div className="text-sm text-muted-foreground">You also get the Matiqs construction procurement marketplace — source materials, run RFQs and manage vendors, alongside contract &amp; IPC billing here.</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">Explore the marketplace <ArrowRight className="w-4 h-4" /></span>
        </a>
      </section>

      {/* CTA */}
      <section className="border-t border-border mt-16">
        <div className="max-w-4xl mx-auto px-5 py-16 text-center">
          <Layers className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">Replace spreadsheets and expensive ERP add-ons</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Any UAE business can raise a compliant tax invoice in minutes — standard or construction — ready for the FTA.</p>
          <Button data-testid="einv-bottom-cta" onClick={start} className="h-12 px-8 mt-7 text-base font-bold gap-2">
            <Zap className="w-4 h-4" /> Start e-invoicing
          </Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
